/*
 * Treat — keep a heading on ONE line by shrinking it, the same size across its group.
 *
 * Markup: data-treat-fit-line="<group>" on the element. Every element sharing a group name gets
 * the size the longest of them needs, so the four image-with-text banners never mix heading
 * sizes. The theme's own size (Typography scale) is the maximum; nothing ever grows.
 *
 * Floor: MIN_SCALE of the theme size (data-treat-fit-min overrides it per group). A heading that would need less than that keeps the floor
 * size and is allowed to WRAP instead — a heading is never cut with an ellipsis.
 *
 * The size is set inline with !important because the .type-* classes are !important
 * (MERCHANT-TYPOGRAPHY-SCALE); only inline !important outranks them.
 *
 * Re-fits on resize, after web fonts load and on Theme Editor section reloads. Scheduled with a
 * timeout rather than requestAnimationFrame, which never fires in a background tab.
 */
(function treatFitLine() {
  if (window.TreatFitLine) return;

  const MIN_SCALE = 0.6;
  const SELECTOR = '[data-treat-fit-line]';
  let timer = null;
  let lastWidth = window.innerWidth;

  function fitGroup(elements) {
    elements.forEach((element) => {
      element.style.removeProperty('font-size');
      element.style.setProperty('white-space', 'nowrap');
    });

    // Available width is the PARENT's content box: in a flex column aligned to the start (the
    // banner content) a nowrap heading grows to its text, so its own width is no limit at all.
    let scale = 1;
    elements.forEach((element) => {
      const parent = element.parentElement;
      const styles = window.getComputedStyle(parent);
      const available =
        parent.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
      const natural = element.scrollWidth;
      if (available > 0 && natural > available) scale = Math.min(scale, available / natural);
    });

    // data-treat-fit-min on the elements overrides the floor (the product title goes to 0.4).
    const minScale = parseFloat(elements[0].getAttribute('data-treat-fit-min')) || MIN_SCALE;
    const fits = scale >= minScale;
    const applied = Math.max(minScale, Math.floor(scale * 1000) / 1000);
    elements.forEach((element) => {
      if (applied < 1) {
        const base = parseFloat(window.getComputedStyle(element).fontSize);
        element.style.setProperty('font-size', `${(base * applied).toFixed(2)}px`, 'important');
      }
      if (!fits) element.style.removeProperty('white-space');
    });
  }

  function fitAll() {
    timer = null;
    const groups = new Map();
    document.querySelectorAll(SELECTOR).forEach((element) => {
      const key = element.getAttribute('data-treat-fit-line') || 'default';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(element);
    });
    groups.forEach((elements) => {
      // data-treat-fit-from="750": below that viewport width the element is left to the theme
      // (wraps at its normal size). A long heading squeezed onto one line of a phone came out
      // barely larger than the body text.
      const active = elements.filter((element) => {
        const from = parseInt(element.getAttribute('data-treat-fit-from') || '0', 10);
        if (window.innerWidth >= from) return true;
        element.style.removeProperty('font-size');
        element.style.removeProperty('white-space');
        return false;
      });
      if (active.length) fitGroup(active);
    });
  }

  function schedule() {
    if (timer) return;
    timer = window.setTimeout(fitAll, 30);
  }

  // Width changes only: fitting changes heights, and mobile browsers fire resize when the
  // address bar shows or hides.
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    schedule();
  });
  document.fonts?.ready.then(schedule);
  document.addEventListener('shopify:section:load', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  window.TreatFitLine = { refresh: schedule };
})();
